import { faCircleLeft, faLock, faLockOpen } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Roles, UserDto } from "../../types/user.types";
import { handleError } from "../../common/utils";
import { useUserService } from "../../services/user.service";
import ConfirmModal from "../../components/confirm-modal";
import { useAuthGuard } from "../../hooks/useAuthGuard";
import { InlineSpinner } from "../../components/inline-spinner";

export default function UserLockouts() {
  useAuthGuard(Roles.Admin);

  const [members, setMembers] = useState<UserDto[]>([]);
  const [reviewers, setReviewers] = useState<UserDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingAction, setPendingAction] = useState<{ userId: number; action: "lock" | "unlock"; email: string } | null>(null);
  const [filter, setFilter] = useState<"all" | "locked" | "unlocked">("all");

  const { getMembers, getReviewers, lockUser, unlockUser } = useUserService();

  const loadUsers = useCallback(async () => {
    setErrorMessage("");
    setLoading(true);

    try {
      const [membersResult, reviewersResult] = await Promise.all([
        getMembers(),
        getReviewers(),
      ]);
      setMembers(membersResult);
      setReviewers(reviewersResult);
    } catch (error) {
      handleError(error, setErrorMessage, () => {});
    } finally {
      setLoading(false);
    }
  }, [getMembers, getReviewers]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleLockUnlock = async () => {
    if (!pendingAction) return;

    setActionLoading(pendingAction.userId);
    setShowConfirmModal(false);

    try {
      if (pendingAction.action === "lock") {
        await lockUser(pendingAction.userId);
      } else {
        await unlockUser(pendingAction.userId);
      }
      await loadUsers();
    } catch (error) {
      handleError(error, setErrorMessage, () => {});
    } finally {
      setActionLoading(null);
      setPendingAction(null);
    }
  };

  const confirmAction = (userId: number, action: "lock" | "unlock", email: string) => {
    setPendingAction({ userId, action, email });
    setShowConfirmModal(true);
  };

  const allUsers = [...members, ...reviewers];
  const filteredUsers = allUsers.filter((user) => {
    if (filter === "locked") return user.isLocked;
    if (filter === "unlocked") return !user.isLocked;
    return true;
  });

  const lockedCount = allUsers.filter((u) => u.isLocked).length;
  const unlockedCount = allUsers.filter((u) => !u.isLocked).length;

  const getRoleName = (roleId: number) => {
    switch (roleId) {
      case Roles.Admin:
        return "Admin";
      case Roles.Manager:
        return "Manager";
      case Roles.Reviewer:
        return "Reviewer";
      default:
        return "Unknown";
    }
  };

  return (
    <div className="container">
      <div className="d-flex flex-row mb-4">
        <div className="me-auto" style={{ marginLeft: "-12px" }}>
          <Link to="/admin" className="btn btn-sw btn-sm btn-outline">
            <FontAwesomeIcon icon={faCircleLeft} className="me-2" />
            Back to Admin
          </Link>
        </div>
      </div>
      <div className="row tablet-view">
        <div className="col-4 tablet-left d-flex flex-column">
          <div className="tablet-buffer d-flex flex-fill flex-column mb-3">
            <span>Administrator</span>
            <h2>User Lockouts</h2>
            <div className="mt-3">
              <div className="d-flex align-items-center mb-2">
                <FontAwesomeIcon icon={faLock} className="text-danger me-2" />
                <span>{lockedCount} Locked</span>
              </div>
              <div className="d-flex align-items-center">
                <FontAwesomeIcon icon={faLockOpen} className="text-success me-2" />
                <span>{unlockedCount} Unlocked</span>
              </div>
            </div>
          </div>
        </div>
        <div className="col-8 tablet-right">
          <div className="tablet-buffer">
            <div className="d-flex left-right mb-3">
              <h3>Manage User Access</h3>
              <div className="btn-group">
                <button
                  className={`btn btn-sm ${filter === "all" ? "btn-sw" : "btn-outline-secondary"}`}
                  onClick={() => setFilter("all")}
                >
                  All ({allUsers.length})
                </button>
                <button
                  className={`btn btn-sm ${filter === "locked" ? "btn-sw" : "btn-outline-secondary"}`}
                  onClick={() => setFilter("locked")}
                >
                  Locked ({lockedCount})
                </button>
                <button
                  className={`btn btn-sm ${filter === "unlocked" ? "btn-sw" : "btn-outline-secondary"}`}
                  onClick={() => setFilter("unlocked")}
                >
                  Unlocked ({unlockedCount})
                </button>
              </div>
            </div>

            {loading ? (
              <div className="text-center py-4">
                <InlineSpinner /> Loading users...
              </div>
            ) : (
              <div style={{ maxHeight: 500, overflowY: "auto", overflowX: "hidden" }}>
                {filteredUsers.length > 0 ? (
                  <table className="sw">
                    <thead>
                      <tr className="d-flex">
                        <th scope="col" className="col-sm-3">Name</th>
                        <th scope="col" className="col-sm-4">Email</th>
                        <th scope="col" className="col-sm-2">Role</th>
                        <th scope="col" className="col-sm-1">Status</th>
                        <th scope="col" className="col-sm-2">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredUsers.map((user) => (
                        <tr key={user.id} className="d-flex border-bottom">
                          <td className="col-sm-3">{user.fullName || "-"}</td>
                          <td className="col-sm-4">{user.email}</td>
                          <td className="col-sm-2">{getRoleName(user.roleId)}</td>
                          <td className="col-sm-1">
                            {user.isLocked ? (
                              <span className="badge bg-danger">Locked</span>
                            ) : (
                              <span className="badge bg-success">Active</span>
                            )}
                          </td>
                          <td className="col-sm-2">
                            {actionLoading === user.id ? (
                              <InlineSpinner />
                            ) : user.isLocked ? (
                              <button
                                className="btn btn-sm btn-outline-success"
                                onClick={() => confirmAction(user.id, "unlock", user.email)}
                                title="Unlock user"
                              >
                                <FontAwesomeIcon icon={faLockOpen} className="me-1" />
                                Unlock
                              </button>
                            ) : (
                              <button
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => confirmAction(user.id, "lock", user.email)}
                                title="Lock user"
                              >
                                <FontAwesomeIcon icon={faLock} className="me-1" />
                                Lock
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="list-group-item list-group-item-light">
                    No users match the current filter
                  </div>
                )}
              </div>
            )}
          </div>

          {errorMessage && (
            <div className="row mt-3">
              <div className="col">
                <div className="alert alert-danger" role="alert">
                  {errorMessage}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <ConfirmModal
        message={
          pendingAction
            ? `Are you sure you want to ${pendingAction.action} ${pendingAction.email}?`
            : ""
        }
        action={handleLockUnlock}
        isOpen={showConfirmModal}
        close={() => {
          setShowConfirmModal(false);
          setPendingAction(null);
        }}
      />
    </div>
  );
}
